import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from '../../../modules/auth/services/auth/auth.service';
import { SessionEntitlementService } from '../../services/session-entitlement/session-entitlement.service';
import {
  PERMISSION_MODULE_SLUG,
  areAnyPermissionsCoveredByEntitlements,
} from '../../config/module-entitlements.config';
import {
  cpPortalModuleLabelFromSlug,
  isCpSubscriptionGuardExemptUrl,
  resolveCpPortalModuleSlugFromUrl,
} from '../../utils/cp-portal-route-module.util';
import {
  isCpPermissionGuardExemptUrl,
  resolveCpRoutePermissionRequirement,
} from '../../utils/cp-portal-route-permission.util';

type AccessDenyReason = 'subscription' | 'permission';

type PermissionAuthResult = {
  allowed: boolean;
  subscriptionDenied: boolean;
};

function matchedPermissions(
  authService: AuthService,
  permissions: string[],
): string[] {
  return permissions.filter((permission) =>
    authService.hasPermission(permission),
  );
}

function passesEntitlementForPermissions(
  permissions: string[],
  entitlements: SessionEntitlementService,
): boolean {
  if (!permissions.length) {
    return true;
  }
  if (!entitlements.entitlementsResolved()) {
    return true;
  }
  return permissions.every((permission) => {
    const slug = PERMISSION_MODULE_SLUG[permission];
    if (!slug) {
      return true;
    }
    return entitlements.canAccessModule(slug);
  });
}

function resolveEntitlementModuleSlugs(
  route: ActivatedRouteSnapshot,
  url: string,
): string[] {
  const fromData = route.data?.['entitlementModulesAny'] as string[] | undefined;
  if (fromData?.length) {
    return fromData;
  }
  const fromUrl = resolveCpPortalModuleSlugFromUrl(url);
  return fromUrl ? [fromUrl] : [];
}

function passesUrlModuleEntitlement(
  url: string,
  entitlements: SessionEntitlementService,
): boolean {
  if (isCpSubscriptionGuardExemptUrl(url)) {
    return true;
  }
  if (!entitlements.entitlementsResolved()) {
    return true;
  }
  const slug = resolveCpPortalModuleSlugFromUrl(url);
  if (!slug) {
    return true;
  }
  return entitlements.canAccessModule(slug);
}

function denySubscriptionAccess(
  router: Router,
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): false {
  const slugs = resolveEntitlementModuleSlugs(route, state.url);
  const moduleSlug = slugs[0] ?? resolveCpPortalModuleSlugFromUrl(state.url);
  const moduleLabel =
    cpPortalModuleLabelFromSlug(moduleSlug) ??
    route.data?.['moduleLabel'] ??
    undefined;

  void router.navigate(['/subscription-access-denied'], {
    queryParams: {
      module: moduleSlug ?? undefined,
      moduleLabel: moduleLabel ?? undefined,
      from: state.url,
    },
  });
  return false;
}

function denyPermissionAccess(
  router: Router,
  state: RouterStateSnapshot,
  pageLabel?: string,
): false {
  void router.navigate(['/access-denied'], {
    queryParams: {
      pageLabel: pageLabel ?? undefined,
      from: state.url,
    },
  });
  return false;
}

function denyAccess(
  router: Router,
  reason: AccessDenyReason,
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  pageLabel?: string,
): false {
  return reason === 'subscription'
    ? denySubscriptionAccess(router, route, state)
    : denyPermissionAccess(router, state, pageLabel);
}

function inheritedStringArrayData(
  route: ActivatedRouteSnapshot,
  key: string,
): string[] {
  let current: ActivatedRouteSnapshot | null = route;
  while (current) {
    const value = current.data?.[key] as string[] | undefined;
    if (value?.length) {
      return value;
    }
    current = current.parent;
  }
  return [];
}

function passesEntitlementModulesGate(
  route: ActivatedRouteSnapshot,
  url: string,
  entitlements: SessionEntitlementService,
): boolean {
  const modules = inheritedStringArrayData(route, 'entitlementModulesAny');
  const moduleSlugs =
    modules.length > 0
      ? modules
      : (() => {
          const fromUrl = resolveCpPortalModuleSlugFromUrl(url);
          return fromUrl ? [fromUrl] : [];
        })();
  if (!moduleSlugs.length || !entitlements.entitlementsResolved()) {
    return true;
  }
  return entitlements.canAccessAnyModule(...moduleSlugs);
}

function authorizePermissions(
  permissions: string[],
  authService: AuthService,
  entitlements: SessionEntitlementService,
  matchMode: 'any' | 'all',
): PermissionAuthResult {
  const hasRbac =
    matchMode === 'any'
      ? authService.hasAnyPermission(permissions)
      : authService.hasPermission(permissions);

  const hasPlanAccess = areAnyPermissionsCoveredByEntitlements(
    permissions,
    (slug) => entitlements.canAccessModule(slug),
    entitlements.entitlementsResolved(),
  );

  if (!hasRbac && !hasPlanAccess) {
    return { allowed: false, subscriptionDenied: false };
  }

  const granted = hasRbac
    ? matchedPermissions(authService, permissions)
    : permissions;

  const entitled = passesEntitlementForPermissions(granted, entitlements);
  if (!entitled) {
    return { allowed: false, subscriptionDenied: true };
  }

  return { allowed: true, subscriptionDenied: false };
}

function applyPermissionAuthResult(
  router: Router,
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  result: PermissionAuthResult,
  pageLabel?: string,
): boolean {
  if (result.allowed) {
    return true;
  }
  denyAccess(
    router,
    result.subscriptionDenied ? 'subscription' : 'permission',
    route,
    state,
    pageLabel,
  );
  return false;
}

function checkPermissionList(
  permissions: string[],
  matchMode: 'any' | 'all',
  router: Router,
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authService: AuthService,
  sessionEntitlements: SessionEntitlementService,
  pageLabel?: string,
): boolean {
  const result = authorizePermissions(
    permissions,
    authService,
    sessionEntitlements,
    matchMode,
  );
  return applyPermissionAuthResult(
    router,
    route,
    state,
    result,
    pageLabel,
  );
}

function checkUrlPermissionRequirement(
  router: Router,
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authService: AuthService,
  sessionEntitlements: SessionEntitlementService,
): boolean {
  if (isCpPermissionGuardExemptUrl(state.url)) {
    return true;
  }
  const urlRequirement = resolveCpRoutePermissionRequirement(state.url);
  if (!urlRequirement) {
    return true;
  }
  return checkPermissionList(
    [...urlRequirement.permissions],
    'any',
    router,
    route,
    state,
    authService,
    sessionEntitlements,
    urlRequirement.pageLabel,
  );
}

function checkRoutePermissionData(
  router: Router,
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authService: AuthService,
  sessionEntitlements: SessionEntitlementService,
): boolean {
  const anyRequired = inheritedStringArrayData(route, 'permissionsAny');
  if (anyRequired.length) {
    return checkPermissionList(
      anyRequired,
      'any',
      router,
      route,
      state,
      authService,
      sessionEntitlements,
    );
  }

  const required = inheritedStringArrayData(route, 'permissions');
  if (!required.length) {
    return true;
  }

  return checkPermissionList(
    required,
    'all',
    router,
    route,
    state,
    authService,
    sessionEntitlements,
  );
}

export const permissionGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const sessionEntitlements = inject(SessionEntitlementService);

  if (!authService.hasFullPortalAccess()) {
    return true;
  }

  if (!passesUrlModuleEntitlement(state.url, sessionEntitlements)) {
    return denyAccess(router, 'subscription', route, state);
  }

  if (!passesEntitlementModulesGate(route, state.url, sessionEntitlements)) {
    return denyAccess(router, 'subscription', route, state);
  }

  if (!checkUrlPermissionRequirement(
    router,
    route,
    state,
    authService,
    sessionEntitlements,
  )) {
    return false;
  }

  return checkRoutePermissionData(
    router,
    route,
    state,
    authService,
    sessionEntitlements,
  );
};
